import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Upload, FileText, Trash2, Loader2, ShieldAlert, X, AlertCircle,
  Zap, Wifi, Phone, Home, Car, Heart, Tv, Dumbbell, Banknote,
  Building2, Baby, HandCoins, MoreHorizontal, Droplet,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => v
  ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
  : '£0.00';

const CATEGORY_META = {
  utilities:    { label: 'Utilities',    icon: Zap,            color: 'text-amber-600 dark:text-amber-400',  bg: 'bg-amber-100 dark:bg-amber-900/30' },
  telecoms:     { label: 'Phone',        icon: Phone,          color: 'text-blue-600 dark:text-blue-400',    bg: 'bg-blue-100 dark:bg-blue-900/30' },
  broadband:    { label: 'Broadband',    icon: Wifi,           color: 'text-cyan-600 dark:text-cyan-400',    bg: 'bg-cyan-100 dark:bg-cyan-900/30' },
  council_tax:  { label: 'Council Tax',  icon: Building2,      color: 'text-purple-600 dark:text-purple-400',bg: 'bg-purple-100 dark:bg-purple-900/30' },
  insurance:    { label: 'Insurance',    icon: Heart,          color: 'text-rose-600 dark:text-rose-400',    bg: 'bg-rose-100 dark:bg-rose-900/30' },
  subscription: { label: 'Subscription', icon: Tv,             color: 'text-pink-600 dark:text-pink-400',    bg: 'bg-pink-100 dark:bg-pink-900/30' },
  gym:          { label: 'Gym',          icon: Dumbbell,       color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-100 dark:bg-emerald-900/30' },
  transport:    { label: 'Transport',    icon: Car,            color: 'text-indigo-600 dark:text-indigo-400',bg: 'bg-indigo-100 dark:bg-indigo-900/30' },
  finance:      { label: 'Finance',      icon: Banknote,       color: 'text-green-600 dark:text-green-400',  bg: 'bg-green-100 dark:bg-green-900/30' },
  mortgage_rent:{ label: 'Mortgage/Rent',icon: Home,           color: 'text-red-600 dark:text-red-400',      bg: 'bg-red-100 dark:bg-red-900/30' },
  childcare:    { label: 'Childcare',    icon: Baby,           color: 'text-orange-600 dark:text-orange-400',bg: 'bg-orange-100 dark:bg-orange-900/30' },
  charity:      { label: 'Charity',      icon: HandCoins,      color: 'text-teal-600 dark:text-teal-400',    bg: 'bg-teal-100 dark:bg-teal-900/30' },
  water:        { label: 'Water',        icon: Droplet,        color: 'text-sky-600 dark:text-sky-400',      bg: 'bg-sky-100 dark:bg-sky-900/30' },
  other:        { label: 'Other',        icon: MoreHorizontal, color: 'text-slate-600 dark:text-slate-400',  bg: 'bg-slate-100 dark:bg-slate-700' },
};

const getCategoryMeta = (cat) => CATEGORY_META[cat] || CATEGORY_META.other;

const BankStatements = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const onPick = (newFiles) => {
    const arr = Array.from(newFiles).filter(f => f.type === 'application/pdf');
    if (arr.length === 0) {
      setError('Only PDF files are supported.');
      return;
    }
    setError('');
    setFiles(prev => [...prev, ...arr]);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.length) onPick(e.dataTransfer.files);
  };

  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const clearAll = () => {
    setFiles([]);
    setResult(null);
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const analyse = async () => {
    if (files.length === 0) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      const res = await fetch(`${API_URL}/api/bank-statements/analyse`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.detail || 'Analysis failed.');
        return;
      }
      setResult(json.data);
    } catch (e) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Group outgoings by category for the breakdown
  const categoryTotals = result?.outgoings?.reduce((acc, o) => {
    acc[o.category] = (acc[o.category] || 0) + (o.estimated_monthly_cost || 0);
    return acc;
  }, {}) || {};
  const sortedCategories = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]);

  return (
    <div className="p-6 space-y-6 animate-fadeIn max-w-6xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Bank Statement Analyser
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Upload client bank statements to extract direct debits and recurring outgoings.
        </p>
      </div>

      {/* Privacy banner */}
      <div className="flex items-start gap-3 px-4 py-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
        <ShieldAlert className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">
            Statements processed in memory only.
          </p>
          <p className="text-xs text-red-600 dark:text-red-500 mt-0.5">
            Files are never saved to the server, the database, or anywhere else. Once analysis completes the file data is wiped from memory. Refresh the page and everything is gone.
          </p>
        </div>
      </div>

      {/* Upload area */}
      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
            Upload Statements
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-10 text-center cursor-pointer hover:border-red-400 hover:bg-red-50/30 dark:hover:bg-red-900/10 transition-colors"
          >
            <Upload className="h-10 w-10 mx-auto mb-3 text-slate-400" />
            <p className="text-sm text-slate-600 dark:text-slate-300 font-medium">
              Drag PDFs here or click to choose files
            </p>
            <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
              Text-based PDF statements only (downloaded from your bank's online portal)
            </p>
            <input
              ref={inputRef}
              type="file"
              accept="application/pdf"
              multiple
              className="hidden"
              onChange={(e) => onPick(e.target.files)}
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-200 dark:border-slate-600">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="h-4 w-4 text-red-600 shrink-0" />
                    <span className="text-sm text-slate-700 dark:text-slate-200 truncate">{f.name}</span>
                    <span className="text-xs text-slate-400 shrink-0">
                      {(f.size / 1024).toFixed(0)} KB
                    </span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(i); }}
                    className="text-slate-400 hover:text-red-600"
                    title="Remove"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Action buttons */}
          {files.length > 0 && (
            <div className="flex items-center gap-2 mt-4">
              <Button
                onClick={analyse}
                disabled={loading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {loading
                  ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analysing...</>
                  : <>Analyse {files.length} Statement{files.length === 1 ? '' : 's'}</>
                }
              </Button>
              <Button variant="outline" onClick={clearAll} disabled={loading}>
                <Trash2 className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <>
          {/* Summary header */}
          <Card className="border-red-200 dark:border-red-800 bg-gradient-to-br from-red-50 to-white dark:from-red-900/10 dark:to-slate-800">
            <CardContent className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Total monthly outgoings</p>
                  <p className="text-3xl font-bold text-red-600 dark:text-red-400 mt-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {fmt(result.total_estimated_monthly)}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Outgoings detected</p>
                  <p className="text-3xl font-bold text-slate-900 dark:text-white mt-1" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                    {result.outgoings?.length || 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">Statement period</p>
                  <p className="text-lg font-semibold text-slate-900 dark:text-white mt-2">
                    {result.statement_period || `~${result.num_months || 0} month${result.num_months === 1 ? '' : 's'}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Category breakdown */}
          {sortedCategories.length > 0 && (
            <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">By Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {sortedCategories.map(([cat, total]) => {
                    const meta = getCategoryMeta(cat);
                    const Icon = meta.icon;
                    const max = sortedCategories[0][1];
                    const pct = max > 0 ? Math.round((total / max) * 100) : 0;
                    return (
                      <div key={cat} className="flex items-center gap-3">
                        <div className={`h-8 w-8 rounded-full ${meta.bg} flex items-center justify-center shrink-0`}>
                          <Icon className={`h-4 w-4 ${meta.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{meta.label}</span>
                            <span className="text-sm font-semibold text-slate-900 dark:text-white">{fmt(total)}</span>
                          </div>
                          <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                            <div className="h-full bg-red-600 rounded-full transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Outgoings list */}
          <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">All Outgoings</CardTitle>
            </CardHeader>
            <CardContent>
              {!result.outgoings || result.outgoings.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400 italic">No recurring outgoings detected.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 dark:border-slate-700">
                        <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Merchant</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Category</th>
                        <th className="text-left py-2 px-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Type</th>
                        <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Typical</th>
                        <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Frequency</th>
                        <th className="text-center py-2 px-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Seen</th>
                        <th className="text-right py-2 px-2 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">Per Month</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.outgoings.map((o, i) => {
                        const meta = getCategoryMeta(o.category);
                        const Icon = meta.icon;
                        return (
                          <tr key={i} className="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-700/30">
                            <td className="py-2.5 px-2 font-medium text-slate-800 dark:text-slate-100">{o.merchant}</td>
                            <td className="py-2.5 px-2">
                              <div className="inline-flex items-center gap-1.5">
                                <Icon className={`h-3.5 w-3.5 ${meta.color}`} />
                                <span className="text-xs text-slate-600 dark:text-slate-300">{meta.label}</span>
                              </div>
                            </td>
                            <td className="py-2.5 px-2 text-xs text-slate-500 dark:text-slate-400">
                              {(o.payment_type || '').replace(/_/g, ' ')}
                            </td>
                            <td className="py-2.5 px-2 text-right text-slate-700 dark:text-slate-200">{fmt(o.typical_amount)}</td>
                            <td className="py-2.5 px-2 text-center text-xs text-slate-500 dark:text-slate-400 capitalize">{o.frequency}</td>
                            <td className="py-2.5 px-2 text-center text-xs text-slate-500 dark:text-slate-400">{o.occurrences}×</td>
                            <td className="py-2.5 px-2 text-right font-semibold text-red-600 dark:text-red-400">{fmt(o.estimated_monthly_cost)}</td>
                          </tr>
                        );
                      })}
                      <tr className="bg-slate-50 dark:bg-slate-700/30 font-bold">
                        <td colSpan={6} className="py-3 px-2 text-right text-slate-700 dark:text-slate-200">Total monthly</td>
                        <td className="py-3 px-2 text-right text-red-600 dark:text-red-400 text-base">{fmt(result.total_estimated_monthly)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default BankStatements;
